import React from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';

interface TabComplementosProps {
  eventoData: {
    complementos: {
      acucar: boolean;
      adocante: boolean;
      gelo: boolean;
      canudo: boolean;
      tipoCopos: string;
      balcao: boolean;
    };
    numeroConvidados: number;
  };
  setEventoData: React.Dispatch<React.SetStateAction<any>>;
}

const TabComplementos: React.FC<TabComplementosProps> = ({ eventoData, setEventoData }) => {
  const complementos = [
    { key: 'acucar', label: 'Açúcar', icon: '🍯', preco: 0.50 },
    { key: 'adocante', label: 'Adoçante', icon: '💊', preco: 0.80 },
    { key: 'gelo', label: 'Gelo', icon: '🧊', preco: 2.00 },
    { key: 'canudo', label: 'Canudo', icon: '🥤', preco: 0.30 },
    { key: 'balcao', label: 'Balcão', icon: '🏪', preco: 150.00, fixo: true }
  ];

  const tiposCopos = [
    { value: 'plastico', label: 'Plástico', preco: 0.30, descricao: 'Econômico e descartável' },
    { value: 'acrilico', label: 'Acrílico', preco: 0.80, descricao: 'Resistente e reutilizável' },
    { value: 'vidro', label: 'Vidro', preco: 1.50, descricao: 'Elegante e sofisticado' }
  ];

  const handleComplementoToggle = (key: string) => {
    setEventoData({
      ...eventoData,
      complementos: {
        ...eventoData.complementos,
        [key]: !eventoData.complementos[key]
      }
    });
  };

  // Calcular custo total dos complementos
  const calcularCustoComplementos = () => {
    let custoTotal = 0;
    
    // Calcular custo dos complementos selecionados
    complementos.forEach(comp => {
      if (eventoData.complementos[comp.key as keyof typeof eventoData.complementos]) {
        if (comp.fixo) {
          custoTotal += comp.preco;
        } else {
          custoTotal += comp.preco * eventoData.numeroConvidados;
        }
      }
    });
    
    // Adicionar custo dos copos
    const tipoCopoSelecionado = tiposCopos.find(tipo => tipo.value === eventoData.complementos.tipoCopos);
    if (tipoCopoSelecionado) {
      custoTotal += tipoCopoSelecionado.preco * eventoData.numeroConvidados;
    }
    
    return custoTotal;
  };

  // Formatar valor em reais
  const formatarValor = (valor: number) => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-6 flex items-center">
        <DynamicIcon name="package" size={24} className="mr-2 text-blue-600" />
        Complementos
      </h2>

      {/* Complementos básicos */}
      <div className="mb-6">
        <h3 className="font-semibold mb-3 text-gray-900">Itens Complementares</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {complementos.map(comp => (
            <label
              key={comp.key}
              className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={eventoData.complementos[comp.key as keyof typeof eventoData.complementos]}
                onChange={() => handleComplementoToggle(comp.key)}
                className="text-blue-600 rounded"
              />
              <span className="text-xl">{comp.icon}</span>
              <div className="flex-1">
                <span className="font-medium">{comp.label}</span>
                <span className="text-sm text-gray-600 ml-2">
                  ({comp.fixo ? formatarValor(comp.preco) : `${formatarValor(comp.preco)}/pessoa`})
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Tipo de Copos */}
      <div className="mb-6">
        <h3 className="font-semibold mb-3 text-gray-900">Tipo de Copos</h3>
        <div className="space-y-2">
          {tiposCopos.map(tipo => (
            <label key={tipo.value} className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="tipoCopos"
                value={tipo.value}
                checked={eventoData.complementos.tipoCopos === tipo.value}
                onChange={(e) => setEventoData({
                  ...eventoData,
                  complementos: {
                    ...eventoData.complementos,
                    tipoCopos: e.target.value
                  }
                })}
                className="text-blue-600"
              />
              <div className="flex-1">
                <span className="font-medium">{tipo.label}</span>
                <span className="text-sm text-gray-600 ml-2">
                  ({formatarValor(tipo.preco)}/unidade)
                </span>
                <p className="text-xs text-gray-500 mt-1">{tipo.descricao}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Resumo de custos */}
      <div className="bg-blue-50 p-4 rounded">
        <h4 className="font-semibold mb-2 text-blue-900">Estimativa de Complementos</h4>
        <div className="space-y-2">
          <p className="text-sm text-blue-800">
            Baseado em {eventoData.numeroConvidados} convidados
          </p>
          
          {/* Detalhamento dos custos */}
          <div className="mt-3 space-y-1 text-sm">
            {complementos.map(comp => {
              if (eventoData.complementos[comp.key as keyof typeof eventoData.complementos]) {
                return (
                  <div key={comp.key} className="flex justify-between">
                    <span className="text-blue-700">{comp.label}:</span>
                    <span className="font-medium">
                      {comp.fixo 
                        ? formatarValor(comp.preco)
                        : formatarValor(comp.preco * eventoData.numeroConvidados)}
                    </span>
                  </div>
                );
              }
              return null;
            }).filter(Boolean)}
            
            {/* Copos */}
            {(() => {
              const tipoCopoSelecionado = tiposCopos.find(tipo => tipo.value === eventoData.complementos.tipoCopos);
              if (tipoCopoSelecionado) {
                return (
                  <div className="flex justify-between">
                    <span className="text-blue-700">Copos ({tipoCopoSelecionado.label}):</span>
                    <span className="font-medium">
                      {formatarValor(tipoCopoSelecionado.preco * eventoData.numeroConvidados)}
                    </span>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Total */}
            <div className="flex justify-between pt-2 mt-2 border-t border-blue-200 font-medium">
              <span className="text-blue-900">Total de Complementos:</span>
              <span className="text-blue-900">{formatarValor(calcularCustoComplementos())}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabComplementos;